import { normalize } from 'normalizr';

import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import { entitySchemas } from '@iconik-io/shared-lib/src/redux/entities/schemas';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';
import {
  AbortUploadError,
  FailedUploadError,
  FileExistsError,
  NoStorageSelectedError,
} from '@iconik-io/uploader/src/errors';
import type {
  IconikUploadScheduler,
  IconikUploadSchedulerEvents,
} from '@iconik-io/uploader/src/scheduler';
import {
  UPLOADER_STATUS_UPLOADING,
  UPLOADER_STATUS_WAITING,
} from '@iconik-io/uploader/src/state';

import type { AppDispatch, GetState } from '../../../store/types';
import { errorMessage } from '../../messages/thunks';
import { uploaderItemErrorSet } from '../items_slice';
import {
  applyFormatComponents,
  applyUploadAcls,
  applyUploadMetadata,
  handleUploadCompleted,
  isAssetFileUpload,
  notifyUploadFailed,
  reportUploadError,
  showCreatedVersion,
} from './upload_side_effects';

const logger = DebugLogger.getInstance('web_uploader');

export type UploadSideEffectsStore = {
  dispatch: AppDispatch;
  getState: GetState;
};

type UploadSideEffectsParams = {
  scheduler: IconikUploadScheduler;
  store: UploadSideEffectsStore;
};

type Events = IconikUploadSchedulerEvents;

export type UploadSideEffects = {
  detach: () => void;
};

const isFileExistsError = (error: Error): boolean =>
  error instanceof FileExistsError ||
  (error instanceof FailedUploadError &&
    error.cause instanceof FileExistsError);

/**
 * Runs web's own side effects of each upload stage. The uploads panel reads
 * the upload states straight from the store the scheduler writes, so nothing
 * here copies them.
 */
export const attachUploadSideEffects = ({
  scheduler,
  store,
}: UploadSideEffectsParams): UploadSideEffects => {
  const { dispatch, getState } = store;

  const isListed = (stateId: string): boolean =>
    Boolean(getState().uploaderStates[stateId]);

  const onStateUpdated = ({ state }: Events['stateUpdated']) => {
    if (
      (state.status === UPLOADER_STATUS_WAITING ||
        state.status === UPLOADER_STATUS_UPLOADING) &&
      getState().uploaderItems[state.id]?.error
    ) {
      dispatch(uploaderItemErrorSet({ stateId: state.id }));
    }
  };

  const onError = ({ state, error }: Events['error']) => {
    if (error instanceof AbortUploadError || !isListed(state.id)) {
      return;
    }
    const fileExists = isFileExistsError(error);
    dispatch(
      uploaderItemErrorSet({
        stateId: state.id,
        error: { message: error.message, fileExists },
      }),
    );
    if (error instanceof NoStorageSelectedError) {
      dispatch(errorMessage({ text: error.message }));
      return;
    }
    reportUploadError(error);
    dispatch(notifyUploadFailed(state, fileExists));
  };

  const onAssetCreated = ({ state, asset }: Events['assetCreated']) => {
    dispatch(updateEntities(normalize(asset, entitySchemas.assets).entities));
    if (!isListed(state.id)) {
      return;
    }
    dispatch(applyUploadAcls(state.id, asset.id));
    dispatch(applyUploadMetadata(state.id, asset.id));
  };

  const onVersionCreated = ({ state }: Events['versionCreated']) => {
    dispatch(showCreatedVersion(state));
  };

  const onFormatCreated = ({ state, format }: Events['formatCreated']) => {
    dispatch(updateEntities(normalize(format, entitySchemas.formats).entities));
    const assetId = state.target.assetId;
    if (!isListed(state.id) || !assetId) {
      return;
    }
    if (state.target.type !== 'newAsset' && isAssetFileUpload(state)) {
      dispatch(applyUploadMetadata(state.id, assetId));
    }
    dispatch(applyFormatComponents(state, assetId, format.id)).catch(
      (error: unknown) => {
        logger.debug('Failed attaching components to the format', error);
      },
    );
  };

  const onFileSetCreated = ({ fileSet }: Events['fileSetCreated']) => {
    dispatch(
      updateEntities(normalize(fileSet, entitySchemas.file_sets).entities),
    );
  };

  const onFileCreated = ({ file: fileEntity }: Events['fileCreated']) => {
    dispatch(
      updateEntities(normalize(fileEntity, entitySchemas.files).entities),
    );
  };

  const onCompleted = ({ state }: Events['completed']) => {
    dispatch(handleUploadCompleted(state)).catch((error: unknown) => {
      logger.debug('Failed finishing the completed upload', error);
    });
  };

  const onLog = ({ level, message, error }: Events['log']) => {
    logger.debug(`[${level}] ${message}`, error ?? '');
  };

  scheduler
    .on('stateUpdated', onStateUpdated)
    .on('error', onError)
    .on('assetCreated', onAssetCreated)
    .on('versionCreated', onVersionCreated)
    .on('formatCreated', onFormatCreated)
    .on('fileSetCreated', onFileSetCreated)
    .on('fileCreated', onFileCreated)
    .on('completed', onCompleted)
    .on('log', onLog);

  const detach = () => {
    scheduler
      .off('stateUpdated', onStateUpdated)
      .off('error', onError)
      .off('assetCreated', onAssetCreated)
      .off('versionCreated', onVersionCreated)
      .off('formatCreated', onFormatCreated)
      .off('fileSetCreated', onFileSetCreated)
      .off('fileCreated', onFileCreated)
      .off('completed', onCompleted)
      .off('log', onLog);
  };

  return { detach };
};
